<template>
  <b-loading v-model="isSavingProcessing" />
  <ProjectPDFExport
    :project="project"
    :exporting-pdf="exportingPdf"
    :with-photos="withPhotos"
    :with-map="withMap"
    :map-data-url="mapDataUrl"
    :images-url="imagesUrl"
    @export-done="onExportPdfDone"
  />
  <div class="validation-handler-container">
    <!-- Contenu principal avec scroll -->
    <div class="content-area">
      <ValidationObserver
        ref="observer"
        v-slot="{ validateWithInfo }"
        class="validation-observer-wrapper"
      >
        <slot class="slot-content"></slot>
        <!-- Div avoiding vue warning -->
        <div v-show="false" @click="newValidationRequest(validateWithInfo)"></div>
      </ValidationObserver>
    </div>

    <!-- Boutons d'action (toujours visible en bas) -->
    <div v-if="!hidden" id="project-save" class="bottom-buttons">
      <b-button
        v-if="
          (project.typeDossier == 'DOSSIER_RENOUVELLEMENT_LIGHT' ||
            project.typeDossier == 'DOSSIER_RENOUVELLEMENT' ||
            project.typeDossier == 'DOSSIER_LABELLISATION') &&
          (project.statut == 'ACCEPTE' || project.statut == 'LABELLISATION_EN_DEVENIR') &&
          hasDocument
        "
        type="is-primary"
        @click="downloadDoc"
      >
        {{ $t("projet.export-document") }}
      </b-button>
      <b-dropdown v-if="project.typeDossier != 'DOSSIER_INSCRIPTION'" position="is-top-left">
        <template #trigger>
          <b-button :label="$t('projet.export-choices')" type="is-info" />
        </template>
        <b-dropdown-item aria-role="listitem" @click="doExportCarnetDeBord">
          {{ $t("projet.export-odt") }}
        </b-dropdown-item>
        <b-dropdown-item aria-role="listitem" @click="doExportPdf(true, true)">
          {{ $t("projet.export-pdf") }}
        </b-dropdown-item>
        <b-dropdown-item aria-role="listitem" @click="doExportPdf(false, true)">
          {{ $t("projet.export-pdf-without-photos") }}
        </b-dropdown-item>
        <b-dropdown-item aria-role="listitem" @click="exportPiecesJointes">
          {{ $t("projet.export-pj-pdf") }}
        </b-dropdown-item>
        <b-dropdown-item aria-role="listitem" @click="exportAllFiles">
          {{ $t("projet.export-all-files") }}
        </b-dropdown-item>
      </b-dropdown>

      <b-button v-else type="is-secondary" @click="doExportPdf(false, false)">
        {{ $t("projet.export-single") }}
      </b-button>

      <b-button
        v-if="!readonly"
        type="is-primary"
        :disabled="!dirty || isSaving"
        @click="saveProject"
      >
        {{ $t("projet.save") }}
      </b-button>

      <!-- Send inscription -->
      <!-- TODO check inscription dates -->
      <b-button
        v-if="
          project.typeDossier == 'DOSSIER_INSCRIPTION' &&
          project.statut == 'BROUILLON' &&
          !readonly &&
          isPorteurProjet
        "
        type="is-primary"
        :disabled="hasErrors || isSaving || !isPorteurProjet || dirty"
        @click="$emit('send-inscription')"
      >
        {{ $t("projet.send-inscription") }}
      </b-button>
      <b-button
        v-if="
          project.typeDossier !== 'DOSSIER_INSCRIPTION' &&
          project.statut == 'BROUILLON' &&
          !readonly &&
          !outsideLabellisationPeriod &&
          isPorteurProjet
        "
        type="is-primary"
        :disabled="!isPorteurProjet"
        @click="sendLabellisation()"
      >
        {{ $t("projet.send-labellisation") }}
      </b-button>
      <!-- Div avoiding vue warning -->
      <div v-show="false" @click="newValidationRequest(validateWithInfo)"></div>
    </div>
  </div>
</template>

<script lang="ts">
import { ProjetAnnuelDTO } from "@/model/bean/GeneratedDTOs"
import ValidationResult, { ValidationIssue } from "@/model/ValidationResult"
import PageChangeForbidder from "@/router/PageChangeForbidder"
import RouterUtils from "@/router/RouterUtils"
import Constants from "@/services/Constants"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { DocumentsService } from "@/services/rest/documents/DocumentsService"
import { ParametersService } from "@/services/rest/parameters/ParametersService"
import { CarnetExportResult, ProjectService } from "@/services/rest/project/ProjectService"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import { BButton, BDropdown, BDropdownItem, BLoading } from "buefy"
import { defineComponent } from "vue"
import ProjectPDFExport from "./ProjectPDFExport.vue"

export class ProjetAnnualValidation {
  invalidFields = new Map<string, ValidationIssue[]>()
  emptyFields = new Map<string, ValidationIssue[]>()
}

export default defineComponent({
  name: "ProjectValidationAndSaveHandler",
  components: {
    BDropdownItem,
    ValidationObserver,
    BButton,
    BDropdown,
    ProjectPDFExport,
    BLoading,
  },
  props: {
    project: {
      type: Object as () => ProjetAnnuelDTO,
      required: true,
    },
    validationActivated: {
      type: Boolean,
      default: true,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
    validationRequestDate: {
      type: Date,
      required: true,
    },
    dirty: {
      type: Boolean,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: true,
    },
    isSaving: {
      type: Boolean,
      required: true,
    },
    isPorteurProjet: {
      type: Boolean,
      required: true,
    },
    pxFromBottom: {
      type: Number,
      required: true,
    },
    hidden: {
      type: Boolean,
      required: true,
    },
    isPdfExportDone: {
      type: Boolean,
      required: true,
    },
    isRequiredExportOdt: {
      type: Boolean,
      default: false,
    },
    imagesUrl: {
      type: Array as () => any[],
      default: () => [],
    },
    mapDataUrl: {
      type: String,
      default: null,
    },
  },

  emits: [
    "send-inscription",
    "validation-changed",
    "save-project",
    "toggle-validation",
    "send-labellisation",
    "do-map-screenshot",
    "clear-map-screenshot",
  ],
  data() {
    return {
      // Services
      infoReportingService: InfoReportingService.INSTANCE,
      parametersService: ParametersService.INSTANCE,
      documentService: DocumentsService.INSTANCE,
      projetService: ProjectService.INSTANCE,

      // Component state
      hasErrors: true,
      hasDocument: false,
      isExportAllFilesProcessing: false,
      isExportPdfProcessing: false,
      isSavingProcessing: false,

      //cache pour éviter un appel à chaque test de validité
      outsideLabellisationPeriod: true,

      exportingPdfDone: false,
      exportingPdf: false,
      pdfExportRequested: false,
      withPhotos: false,
      withMap: false,
    }
  },
  watch: {
    isSaving(newVal: boolean): void {
      this.isSavingProcessing = newVal
    },
    validationRequestDate: {
      async handler(): Promise<void> {
        this.hasErrors = false
        if (this.validationActivated) {
          //@ts-ignore
          const validationResult: ValidationResult = await this.$refs.observer.validateWithInfo()
          const projetAnnuelValidation =
            this.convertValidationResultToProjectAnnuelValidation(validationResult)

          this.hasErrors = this.hasErrorsIgnoringParticipants(projetAnnuelValidation)

          this.$emit("validation-changed", projetAnnuelValidation)
        }
      },
    },
    dirty: {
      handler(): void {
        if (this.dirty) {
          // If dirty, page change is forbidden
          PageChangeForbidder.INSTANCE.forbidPageChange(
            this.showDirtyPopup,
            this.$t("projet.dirtyPopup.message").toString(),
            true,
          )
        } else {
          // If not, page change is allowed again
          PageChangeForbidder.INSTANCE.allowPageChange()
        }
      },
    },
  },

  async mounted(): Promise<void> {
    const parameter = await this.parametersService.getParametrage(this.project.grae.id)
    const now = new Date()

    if (now < parameter.debutLabellisation) {
      this.outsideLabellisationPeriod = true
    } else this.outsideLabellisationPeriod = now > parameter.finLabellisation

    await this.documentExist()
  },
  unmounted(): void {
    PageChangeForbidder.INSTANCE.allowPageChange()
  },
  methods: {
    /**
     * Indicates if given validation message descriptes a warning (will be considered as error otherwise)
     */
    isWarning(errorMessage: string): boolean {
      return (
        errorMessage == this.$t("validation-input-invalid-required") ||
        errorMessage == this.$t("projet.inscription.finalisation-inscription.certified-error")
      )
    },

    async documentExist(): Promise<void> {
      this.hasDocument = await this.documentService.getDocumentByTypeAndAnneScolaire(
        this.project.natureAireEducative,
        this.project.anneeScolaire,
      )
    },

    shouldIgnore(groupId: string, _errorMessage: string): boolean {
      // Ignore "new financement" errors
      return groupId == "financement" || groupId == "validation-to-ignore"
    },

    async downloadDoc(): Promise<void> {
      let base =
        window.location.origin +
        "/api/v2/document/" +
        this.project.anneeScolaire +
        "/" +
        this.project.natureAireEducative
      if (this.project.lastEvaluationMention == "LABELLISATION_EN_DEVENIR") {
        base = base + " en devenir"
      }
      window.location.href = base
    },

    hasErrorsIgnoringParticipants(projetAnnuelValidation: ProjetAnnualValidation): boolean {
      let result =
        projetAnnuelValidation.invalidFields.size > 0 || projetAnnuelValidation.emptyFields.size > 0

      // If the only warning is that a referent is missing
      // We allow dépot without referent (a warning will be displayed in ProjetInscriptionFinalisation)
      const allParticipantsErrors = projetAnnuelValidation.emptyFields.get("participants")

      if (allParticipantsErrors && allParticipantsErrors.length == 1) {
        const missingReferentsError = Array.from(allParticipantsErrors).filter((issue) => {
          return issue.field == "participants_referents"
        })
        if (missingReferentsError && missingReferentsError.length > 0) {
          result =
            projetAnnuelValidation.invalidFields.size > 0 ||
            projetAnnuelValidation.emptyFields.size > 1
        }
      }
      return result
    },

    convertValidationResultToProjectAnnuelValidation(
      validationResult: ValidationResult,
    ): ProjetAnnualValidation {
      // Step 1: create empty ProjetAnnualValidation
      const projetAnnuelValidation = new ProjetAnnualValidation()
      projetAnnuelValidation.invalidFields = new Map<string, ValidationIssue[]>()
      projetAnnuelValidation.emptyFields = new Map<string, ValidationIssue[]>()

      // Step2: Browse through validation errors and sort them by group and severity
      Object.keys(validationResult.errors).forEach((fieldInError) => {
        const validationErrorsForField: Array<string> =
          validationResult.errors[fieldInError.toString()]
        if (validationErrorsForField && validationErrorsForField.length > 0) {
          let groupId = ""
          if (fieldInError.split("_").length > 1) {
            groupId = fieldInError.split("_")[0]
          }
          if (groupId) {
            // Errors could be warning (empty fields)
            if (this.shouldIgnore(groupId, validationErrorsForField[0])) {
              // Ignore error
            } else if (this.isWarning(validationErrorsForField[0])) {
              let existingErrors = projetAnnuelValidation.emptyFields.get(groupId)
              if (existingErrors == undefined) {
                existingErrors = []
              }
              existingErrors.push(new ValidationIssue(fieldInError, validationErrorsForField[0]))
              projetAnnuelValidation.emptyFields.set(groupId, existingErrors)
            } else {
              // Or severe errors
              let existingErrors = projetAnnuelValidation.invalidFields.get(groupId)
              if (existingErrors == undefined) {
                existingErrors = []
              }
              existingErrors.push(new ValidationIssue(fieldInError, validationErrorsForField[0]))
              projetAnnuelValidation.invalidFields.set(groupId, existingErrors)
            }
          }
        }
      })
      return projetAnnuelValidation
    },

    async saveProject(): Promise<void> {
      // Step 1: validation before save
      //@ts-ignore
      const validationResult: ValidationResult = await this.$refs.observer.validateWithInfo()
      const projetAnnuelValidation =
        this.convertValidationResultToProjectAnnuelValidation(validationResult)
      this.hasErrors = this.hasErrorsIgnoringParticipants(projetAnnuelValidation)

      // If there are big errors, prevent from saving
      if (projetAnnuelValidation && projetAnnuelValidation.invalidFields.size > 0) {
        const firstFieldInErrorIterator = projetAnnuelValidation.invalidFields.keys().next()
        const firstFieldInError =
          firstFieldInErrorIterator && firstFieldInErrorIterator.value
            ? firstFieldInErrorIterator.value.toString()
            : ""
        this.infoReportingService.error(
          this.$t("projet.cannot-save", [this.$t(firstFieldInError)]).toString(),
          new Error(),
        )
      } else {
        // Otherwise save project
        this.$emit("save-project")
        PageChangeForbidder.INSTANCE.allowPageChange()
      }

      if (this.validationActivated) {
        // And if necessary, refresh menu with validation result
        this.$emit("validation-changed", projetAnnuelValidation)
      }
    },

    showDirtyPopup(next: () => void): void {
      this.infoReportingService.dialogWithCancel(
        this.$t("projet.dirtyPopup.title").toString(),
        this.$t("projet.dirtyPopup.message").toString(),
        this.$t("projet.dirtyPopup.discard-and-continue").toString(),
        async () => {
          next()
        },
        () => {
          // Nothing to do
        },
        "is-danger",
      )
    },

    async saveBeforeLabellisation(): Promise<boolean> {
      // Step 1: validation before save
      //@ts-ignore
      const validationResult: ValidationResult = await this.$refs.observer.validateWithInfo()
      const projetAnnuelValidation =
        this.convertValidationResultToProjectAnnuelValidation(validationResult)
      this.hasErrors = this.hasErrorsIgnoringParticipants(projetAnnuelValidation)

      // If there are big errors, prevent from saving
      if (projetAnnuelValidation.invalidFields.size > 0) {
        return false
      } else {
        await this.projetService.saveProject(this.project)
        PageChangeForbidder.INSTANCE.allowPageChange()
        return true
      }
    },

    async sendLabellisation(): Promise<void> {
      if (await this.saveBeforeLabellisation()) {
        if (!this.validationActivated) {
          this.$emit("toggle-validation")

          // Step 1: validation before save
          //@ts-ignore
          const validationResult: ValidationResult = await this.$refs.observer.validateWithInfo()
          const projetAnnuelValidation =
            this.convertValidationResultToProjectAnnuelValidation(validationResult)
          this.hasErrors =
            projetAnnuelValidation.invalidFields.size > 0 ||
            projetAnnuelValidation.emptyFields.size > 0
        }
        if (this.hasErrors) {
          this.infoReportingService.error(
            this.$t("projet.cannot-submit-labellisation").toString(),
            new Error(),
          )
          await this.$router.push(
            RouterUtils.getRouteForProjectDetailsAideLabOn(this.project.projetId, this.project.id),
          )
        } else {
          this.$emit("send-labellisation")
        }
      } else {
        this.infoReportingService.error(
          this.$t("projet.cannot-submit-labellisation").toString(),
          new Error(),
        )
        await this.$router.push(
          RouterUtils.getRouteForProjectDetailsAideLabOn(this.project.projetId, this.project.id),
        )
      }
    },

    async exportAllFiles(): Promise<void> {
      try {
        if (!this.isExportAllFilesProcessing) {
          this.isExportAllFilesProcessing = true
          this.$emit("do-map-screenshot")

          this.infoReportingService.success(this.$t("file.download.start").toString())
          let isSuccess = true
          const errors: string[] = []

          // Lancer en parallèle : PDF avec photos + fichiers backend
          const parallelTasks = [
            // Tâche 1: PDF avec photos
            this.wrapAsync(async () => {
              const pdfWithPhotosSuccess = await this.exportPdf(true)
              if (!pdfWithPhotosSuccess) {
                const message = "Erreur lors du téléchargement du PDF avec photos"
                console.error(message)
                throw new Error(message)
              }
              console.log("Téléchargement du PDF avec photos terminé")
            }),
            // Tâche 2: Carnet de bord
            this.wrapAsync(async () => {
              await this.exportCarnetDeBord()
              console.log("Téléchargement du carnet de bord terminé")
            }),
            // Tâche 3: Pièces jointes
            this.wrapAsync(async () => {
              await this.exportPiecesJointes()
              console.log("Téléchargement des pièces jointes terminé")
            }),
          ]

          const parallelResults = await Promise.allSettled(parallelTasks.map((task) => task()))

          // Vérifier les résultats
          parallelResults.forEach((result, index) => {
            const taskNames = ["PDF avec photos", "carnet de bord", "pièces jointes"]
            if (result.status !== "fulfilled") {
              isSuccess = false
              errors.push(taskNames[index])
              console.error(
                `Échec du téléchargement ${taskNames[index]}:`,
                result.status === "rejected" ? result.reason : "unknown",
              )
            }
          })

          // Attendre 200ms après la fin du PDF avec photos avant de lancer le second PDF
          await new Promise((resolve) => setTimeout(resolve, 500))

          // Lancer le PDF sans photos
          try {
            const pdfWithoutPhotosSuccess = await this.exportPdf(false)
            if (!pdfWithoutPhotosSuccess) {
              throw new Error("Erreur lors du téléchargement du PDF sans photos")
            } else {
              console.log("Téléchargement PDF sans photos terminé")
            }
          } catch (error) {
            console.error((error as Error).message, error)
            errors.push("PDF sans photos")
            isSuccess = false
          }

          if (isSuccess) {
            this.infoReportingService.success(this.$t("file.download.end-success").toString())
          } else {
            const errorMessage = errors.length > 0 ? ` (${errors.join(", ")})` : ""
            this.infoReportingService.warn(
              this.$t("file.download.end-error").toString() + errorMessage,
              new Error(this.$t("file.download.error-message").toString()),
            )
          }
        }
      } finally {
        this.isExportAllFilesProcessing = false
        this.$emit("clear-map-screenshot")
      }
    },

    async exportPdf(withPhotos: boolean): Promise<boolean> {
      // Marquer le début du processus
      this.isExportPdfProcessing = true

      // Émettre immédiatement pour déclencher le loader
      this.initializeExportPdf(withPhotos, true)

      // Attendre un tick pour laisser le temps au loader de s'afficher
      await this.$nextTick()

      // Attendre que isPdfExportDone devienne true ou timeout
      const startTime = Date.now()
      const timeoutMs = 20000 // 20 secondes pour connexions très lentes

      // D'abord, attendre que isPdfExportDone soit réinitialisé à false (si nécessaire)
      const waitForReset = new Promise<void>((resolve) => {
        const checkResetInterval = setInterval(() => {
          if (!this.isPdfExportDone || Date.now() - startTime > 2000) {
            clearInterval(checkResetInterval)
            resolve()
          }
        }, 50)
      })

      await waitForReset

      // Maintenant, attendre que isPdfExportDone devienne true (le nouveau export)
      const waitForExport = new Promise<boolean>((resolve) => {
        const checkInterval = setInterval(() => {
          if (this.exportingPdfDone) {
            console.log("Export PDF terminé avec succès")
            clearInterval(checkInterval)
            resolve(true)
          } else if (Date.now() - startTime > timeoutMs) {
            clearInterval(checkInterval)
            console.warn(
              `Timeout après ${timeoutMs}ms pour l'export PDF (${withPhotos ? "avec" : "sans"} photos). isPdfExportDone = ${this.isPdfExportDone}`,
            )
            resolve(false)
          }
        }, 100)
      })

      const success = await waitForExport
      this.isExportPdfProcessing = false

      return success
    },

    async doExportCarnetDeBord(): Promise<void> {
      try {
        this.$emit("do-map-screenshot")
        await this.exportCarnetDeBord()
      } finally {
        this.$emit("clear-map-screenshot")
      }
    },

    async exportCarnetDeBord(): Promise<void> {
      const carnetExportResult = await this.downloadCarnetDeBord()

      if (carnetExportResult) {
        const fileName = carnetExportResult.fileName
        this.createDownloadLink(
          URL.createObjectURL(carnetExportResult.blob),
          fileName ? fileName : "carnet_de_bord",
        )
      } else {
        throw new Error("Erreur lors du téléchargement du carnet de bord.")
      }
    },

    async exportPiecesJointes(): Promise<void> {
      const fileURL = await this.downloadPiecesJointes()
      if (fileURL) {
        this.createDownloadLink(fileURL, "fichier.zip")
      } else {
        throw new Error("Erreur lors du téléchargement des pièces jointes.")
      }
    },

    async downloadCarnetDeBord(): Promise<CarnetExportResult> {
      let count = 0
      // On attend le screenshot maximum pendant 6s (connexions lentes)
      while (!this.mapDataUrl && count < 40) {
        await new Promise((resolve) => setTimeout(resolve, 150))
        count++
      }

      if (!this.mapDataUrl) {
        console.warn("Timeout lors de la génération du screenshot de la carte")
      }

      const carnetExportResult = await this.projetService.downloadCarnetDeBord(
        this.project.id,
        this.mapDataUrl,
      )

      return carnetExportResult
    },

    async downloadPiecesJointes(): Promise<string> {
      return Constants.apiUrlv2("projets/" + this.project.id + "/fichiers-zip")
    },

    wrapAsync(asyncFn: () => Promise<void>): () => Promise<void> {
      return async () => {
        try {
          await asyncFn()
        } catch (error) {
          console.error("Erreur = ", error)
          throw error
        }
      }
    },

    createDownloadLink(href: string, fileName: string): void {
      const downloadLink = document.createElement("a")
      downloadLink.href = href
      downloadLink.download = fileName
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
    },

    doExportPdf(withPhotos: boolean, withMap: boolean): void {
      this.initializeExportPdf(withPhotos, withMap)
      if (withMap && !this.mapDataUrl) {
        this.$emit("do-map-screenshot")
      }
    },

    initializeExportPdf(withPhotos: boolean, withMap: boolean): void {
      // Réinitialiser l'état pour permettre un nouveau cycle d'export
      this.exportingPdfDone = false
      // Afficher le loader immédiatement pour un meilleur feedback utilisateur
      this.exportingPdf = true
      // Préparer le nouveau export
      this.pdfExportRequested = true
      this.withPhotos = withPhotos
      this.withMap = withMap
    },

    onExportPdfDone(): void {
      this.exportingPdfDone = true
      this.exportingPdf = false
      this.pdfExportRequested = false
      this.withPhotos = false
      this.withMap = false
    },
  },
})
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

.validation-handler-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.header-area {
  flex-shrink: 0;
}

.content-area {
  flex: 1;
  height: 100%;
  min-height: 0; // Important pour que le scroll fonctionne correctement
}

.validation-observer-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.slot-content {
  flex: 1;
}

.bottom-buttons {
  flex-shrink: 0;
  z-index: 2;
  background-color: $modal-background-color;
  border-top: 1px solid $bookmarkcard-border-color;
  height: 66px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 1vw;
  box-shadow: rgb(0 0 0 / 10%) -1px -3px 10px 5px;

  .button {
    margin-left: 20px;
  }
}

#project-save {
  width: 100%;
}
</style>
